import { ApiError } from "@aurumiq/api-client";
import { Button } from "@aurumiq/ui/components/button";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { money } from "@aurumiq/ui/lib/format";
import { Pencil, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { usePermission } from "@/auth/use-permission";
import { errorMessage } from "@/lib/api";
import type { AdminPlan } from "@/lib/types";
import { PlanDialog } from "./plan-dialog";
import { useAdminPlans, useSyncPlans } from "./queries";

const col = createColumnHelper<AdminPlan>();

function StripeRef({ id }: { id: string | null }) {
  return id ? (
    <span className="font-mono text-xs">{id}</span>
  ) : (
    <StatusTag variant="warn">NOT SYNCED</StatusTag>
  );
}

export function PlansTab() {
  const canWrite = usePermission("billing:write");
  const plans = useAdminPlans();
  const sync = useSyncPlans();
  const [editing, setEditing] = useState<AdminPlan | null>(null);

  const columns = useMemo(
    () =>
      [
        col.accessor("name", {
          header: "Plan",
          cell: (c) => (
            <div>
              <div className="font-semibold">{c.getValue()}</div>
              <div className="font-mono text-muted-foreground text-xs">
                {c.row.original.code} · {c.row.original.tier}
              </div>
            </div>
          ),
        }),
        col.accessor("seats", { header: "Seats", meta: { numeric: true } }),
        col.accessor("price_month", {
          header: "Monthly",
          meta: { numeric: true },
          cell: (c) => money(c.getValue(), c.row.original.currency, 0),
        }),
        col.accessor("price_year", {
          header: "Yearly",
          meta: { numeric: true },
          cell: (c) => money(c.getValue(), c.row.original.currency, 0),
        }),
        col.accessor("features", {
          header: "Features",
          enableSorting: false,
          cell: (c) => (
            <span className="line-clamp-2 max-w-xs text-muted-foreground text-xs">
              {c.getValue().join(" · ") || "—"}
            </span>
          ),
        }),
        col.accessor("stripe_product_id", {
          header: "Stripe",
          cell: (c) => <StripeRef id={c.getValue()} />,
        }),
        col.accessor("active", {
          header: "Active",
          cell: (c) =>
            c.getValue() ? <StatusTag variant="ok">YES</StatusTag> : <StatusTag>NO</StatusTag>,
        }),
        col.accessor("sort_order", { header: "Order", meta: { numeric: true } }),
        col.display({
          id: "actions",
          header: "",
          meta: { numeric: true },
          cell: (c) =>
            canWrite ? (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${c.row.original.name}`}
                onClick={() => setEditing(c.row.original)}
              >
                <Pencil /> Edit
              </Button>
            ) : null,
        }),
      ] as ColumnDef<AdminPlan, unknown>[],
    [canWrite],
  );

  const notConfigured =
    sync.error instanceof ApiError &&
    (sync.error.code === "STRIPE_NOT_CONFIGURED" || sync.error.status === 503);

  return (
    <>
      {notConfigured ? (
        <div
          role="status"
          className="mb-3 rounded-lg border border-warning/40 bg-warning-bg px-3 py-2 text-sm text-warning"
        >
          Stripe is not configured on the API (missing secret key). Plans are saved locally but
          cannot be synced until <code className="font-mono">STRIPE_SECRET_KEY</code> is set.
        </div>
      ) : sync.isError ? (
        <div
          role="alert"
          className="mb-3 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-danger text-sm"
        >
          Sync failed: {errorMessage(sync.error)}
        </div>
      ) : null}
      {sync.data?.errors.length ? (
        <ul className="mb-3 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-danger text-sm">
          {sync.data.errors.map((e) => (
            <li key={`${e.code}-${e.message}`}>
              {e.code ? <span className="font-mono">{e.code}: </span> : null}
              {e.message}
            </li>
          ))}
        </ul>
      ) : null}
      <DataTable
        columns={columns}
        data={[...(plans.data?.items ?? [])].sort((a, b) => a.sort_order - b.sort_order)}
        isLoading={plans.isPending}
        getRowId={(p) => p.code}
        emptyMessage={
          plans.isError ? `Could not load plans: ${errorMessage(plans.error)}` : "No plans."
        }
        toolbar={
          canWrite ? (
            <Button
              variant="outline"
              onClick={() => sync.mutate(undefined)}
              disabled={sync.isPending}
            >
              <RefreshCw className={sync.isPending ? "animate-spin" : undefined} />
              {sync.isPending ? "Syncing…" : "Sync to Stripe"}
            </Button>
          ) : null
        }
      />
      <PlanDialog plan={editing} onOpenChange={(o) => !o && setEditing(null)} />
    </>
  );
}
